import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import Select from "react-select";
import { Dialog } from "@mui/material";
import toast from "react-hot-toast";
import CloseIcon from "@mui/icons-material/Close";

import { PUT_USER } from "../../api/user";
import { toE164, PHONE_EXAMPLE, PHONE_ERROR } from "../../utils/phone";
import { getCompanyUsers } from "../../redux/company/actions";
import SoftBox from "../../components/SoftBox";
import SoftTypography from "../../components/SoftTypography";
import SoftButton from "../../components/SoftButton";

const BRAND = "var(--gmb-primary, #1B598E)";

// Selected → brand tint, hovered → faint gray, otherwise transparent.
const optionBackground = (state) => {
  if (state.isSelected) return "rgba(27,89,142,0.10)";
  if (state.isFocused) return "rgba(15,23,42,0.05)";
  return "transparent";
};

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Days in each month (Feb capped at 29 — no year is stored, so leap years count).
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const monthOptions = MONTHS.map((m, i) => ({ value: i + 1, label: m }));

// Shared react-select styling — light control, frosted-glass menu, brand focus.
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "42px",
    borderRadius: "8px",
    background: "#FFFFFF",
    border: state.isFocused ? `1.5px solid ${BRAND}` : "1px solid #D0D5DD",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(27,89,142,0.08)" : "none",
    fontFamily: "DM Sans",
    fontSize: "13.5px",
    cursor: "pointer",
    "&:hover": { borderColor: state.isFocused ? BRAND : "#98A2B3" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 10px" }),
  placeholder: (base) => ({ ...base, color: "#98A2B3", fontWeight: 400 }),
  singleValue: (base) => ({ ...base, color: "#191D23", fontWeight: 500 }),
  dropdownIndicator: (base) => ({ ...base, padding: "0 8px", color: "#98A2B3" }),
  indicatorSeparator: () => ({ display: "none" }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({
    ...base,
    borderRadius: "8px",
    background: "rgba(255,255,255,0.72)",
    backdropFilter: "blur(26px) saturate(185%)",
    WebkitBackdropFilter: "blur(26px) saturate(185%)",
    border: "1px solid rgba(255,255,255,0.6)",
    padding: "6px",
    boxShadow: "0 16px 40px -12px rgba(16,24,40,0.22), inset 0 1px 0 rgba(255,255,255,0.7)",
    overflow: "hidden",
  }),
  menuList: (base) => ({ ...base, padding: 0, maxHeight: "220px" }),
  option: (base, state) => ({
    ...base,
    fontFamily: "DM Sans",
    fontSize: "13.5px",
    fontWeight: state.isSelected ? 600 : 500,
    borderRadius: "6px",
    padding: "9px 10px",
    cursor: "pointer",
    background: optionBackground(state),
    color: state.isSelected ? BRAND : "#475467",
    "&:active": { background: "rgba(27,89,142,0.16)" },
  }),
};

const labelStyle = {
  fontFamily: "DM Sans",
  fontSize: "12.5px",
  fontWeight: 600,
  color: "#344054",
  marginBottom: "6px",
};

// Native <input> styled to match the react-selects above.
const inputStyle = {
  width: "100%",
  height: "42px",
  borderRadius: "8px",
  border: "1px solid #D0D5DD",
  padding: "0 12px",
  fontFamily: "DM Sans",
  fontSize: "13.5px",
  color: "#191D23",
  background: "#FFFFFF",
  outline: "none",
  boxSizing: "border-box",
};

/**
 * Edit details — admin-facing modal opened from a player row's ⋯ menu.
 * Lets an admin fill in or fix a player's contact + personal dates. These feed
 * the automation engine (birthday / work-anniversary triggers) and the same
 * fields are self-editable by players in Account Settings.
 */
const EditPlayerDetails = ({ open, onClose, user }) => {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);

  const [phone, setPhone] = useState(user?.phoneNumber || "");
  const [phoneExempt, setPhoneExempt] = useState(Boolean(user?.phoneExempt));
  const [month, setMonth] = useState(user?.birthday?.month || null);
  const [day, setDay] = useState(user?.birthday?.day || null);
  const [hireDate, setHireDate] = useState(
    user?.hireDate ? String(user.hireDate).slice(0, 10) : ""
  );

  // Re-seed local state whenever a different player is opened.
  React.useEffect(() => {
    if (!open) return;
    setPhone(user?.phoneNumber || "");
    setPhoneExempt(Boolean(user?.phoneExempt));
    setMonth(user?.birthday?.month || null);
    setDay(user?.birthday?.day || null);
    setHireDate(user?.hireDate ? String(user.hireDate).slice(0, 10) : "");
  }, [open, user]);

  // Day list narrows to the selected month (defaults to 31 when none picked).
  const dayOptions = useMemo(() => {
    const max = month ? DAYS_IN_MONTH[month - 1] : 31;
    return Array.from({ length: max }, (_, i) => ({ value: i + 1, label: String(i + 1) }));
  }, [month]);

  const handleMonth = (opt) => {
    const m = opt?.value || null;
    setMonth(m);
    // Clamp the day if the new month is shorter (e.g. was 31, switched to Feb).
    if (m && day && day > DAYS_IN_MONTH[m - 1]) setDay(DAYS_IN_MONTH[m - 1]);
  };

  const handleSave = async () => {
    // Birthday is all-or-nothing: both month and day, or neither.
    if ((month && !day) || (!month && day)) {
      toast.error("Pick both a birthday month and day, or leave both empty.");
      return;
    }
    // Normalise to E.164 so the server's libphonenumber validation passes.
    // A blank number is allowed only when the player is phone-exempt.
    const trimmedPhone = phone.trim();
    let normalizedPhone = "";
    if (trimmedPhone) {
      const e164 = toE164(trimmedPhone);
      if (!e164) {
        toast.error(PHONE_ERROR);
        return;
      }
      normalizedPhone = e164;
    } else if (!phoneExempt) {
      toast.error(
        `Add a mobile number (e.g. ${PHONE_EXAMPLE}) or mark this player phone-exempt.`
      );
      return;
    }

    setSaving(true);
    try {
      const payload = {
        phoneNumber: normalizedPhone,
        phoneExempt,
        birthday: month && day ? { month, day } : { month: null, day: null },
        hireDate: hireDate || null,
      };
      // updateMe = false: an admin is editing another player, not their own row.
      await PUT_USER(user.userId, payload, false);
      dispatch(getCompanyUsers());
      toast.success("Player details saved");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Couldn't save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const fullName = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{
        ".MuiPaper-root": {
          borderRadius: "8px",
          background: "rgba(255, 255, 255, 0.72)",
          backdropFilter: "blur(18px) saturate(160%)",
          WebkitBackdropFilter: "blur(18px) saturate(160%)",
          boxShadow:
            "0px 24px 48px -8px rgba(16,24,40,0.18), inset 0px 1px 0px 0px rgba(255,255,255,0.7)",
          border: "1px solid rgba(255,255,255,0.6)",
          padding: 0,
          maxWidth: { mini: "92%", xs: "92%", sm: "460px" },
          width: "100%",
          overflow: "visible",
        },
      }}
    >
      {/* Header */}
      <SoftBox
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          padding: "20px 22px 6px",
        }}
      >
        <SoftBox>
          <SoftTypography sx={{ fontSize: "17px", fontWeight: 600, color: "#101828" }}>
            Edit details
          </SoftTypography>
          {fullName && (
            <SoftTypography
              sx={{ fontSize: "12.5px", fontWeight: 400, color: "#667085", mt: "2px" }}
            >
              {fullName}
            </SoftTypography>
          )}
        </SoftBox>
        <SoftBox
          component="button"
          type="button"
          aria-label="Close"
          onClick={onClose}
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            border: 0,
            cursor: "pointer",
            background: "#F2F4F7",
            color: "#667085",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            "& svg": { fontSize: "1rem !important" },
            "&:hover": { background: "#EAECF0" },
          }}
        >
          <CloseIcon />
        </SoftBox>
      </SoftBox>

      {/* Body */}
      <SoftBox sx={{ padding: "12px 22px 4px" }}>
        {/* Phone */}
        <SoftBox sx={{ mb: "16px" }}>
          <SoftTypography sx={labelStyle}>Phone number</SoftTypography>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={PHONE_EXAMPLE}
            style={inputStyle}
            onFocus={(e) => {
              e.target.style.border = `1.5px solid ${BRAND}`;
              e.target.style.boxShadow = "0 0 0 3px rgba(27,89,142,0.08)";
            }}
            onBlur={(e) => {
              e.target.style.border = "1px solid #D0D5DD";
              e.target.style.boxShadow = "none";
            }}
          />
          <SoftTypography
            sx={{ fontFamily: "DM Sans", fontSize: "11.5px", color: "#98A2B3", mt: "5px" }}
          >
            Used for SMS alerts once texting is connected.
          </SoftTypography>

          <SoftBox
            component="label"
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: "8px",
              mt: "10px",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={phoneExempt}
              onChange={(e) => setPhoneExempt(e.target.checked)}
              style={{ marginTop: "2px", accentColor: "#1B598E" }}
            />
            <SoftBox>
              <SoftTypography
                sx={{ fontFamily: "DM Sans", fontSize: "12.5px", fontWeight: 600, color: "#344054" }}
              >
                Phone number not required
              </SoftTypography>
              <SoftTypography
                sx={{ fontFamily: "DM Sans", fontSize: "11.5px", color: "#98A2B3", mt: "2px" }}
              >
                This player won&apos;t be prompted for a phone and won&apos;t
                receive SMS alerts.
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        </SoftBox>

        {/* Birthday — month + day, no year */}
        <SoftBox sx={{ mb: "16px" }}>
          <SoftTypography sx={labelStyle}>Birthday</SoftTypography>
          <SoftBox sx={{ display: "flex", gap: "10px" }}>
            <SoftBox sx={{ flex: 1.4 }}>
              <Select
                options={monthOptions}
                value={monthOptions.find((o) => o.value === month) || null}
                onChange={handleMonth}
                placeholder="Month"
                isClearable
                isSearchable={false}
                menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                menuPlacement="auto"
                styles={selectStyles}
              />
            </SoftBox>
            <SoftBox sx={{ flex: 1 }}>
              <Select
                options={dayOptions}
                value={dayOptions.find((o) => o.value === day) || null}
                onChange={(opt) => setDay(opt?.value || null)}
                placeholder="Day"
                isClearable
                isSearchable={false}
                menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                menuPlacement="auto"
                styles={selectStyles}
              />
            </SoftBox>
          </SoftBox>
          <SoftTypography
            sx={{ fontFamily: "DM Sans", fontSize: "11.5px", color: "#98A2B3", mt: "5px" }}
          >
            No year needed — just the day we celebrate.
          </SoftTypography>
        </SoftBox>

        {/* Hire date — full date, drives work anniversaries */}
        <SoftBox sx={{ mb: "4px" }}>
          <SoftTypography sx={labelStyle}>Hire date</SoftTypography>
          <input
            type="date"
            value={hireDate}
            onChange={(e) => setHireDate(e.target.value)}
            style={inputStyle}
            onFocus={(e) => {
              e.target.style.border = `1.5px solid ${BRAND}`;
              e.target.style.boxShadow = "0 0 0 3px rgba(27,89,142,0.08)";
            }}
            onBlur={(e) => {
              e.target.style.border = "1px solid #D0D5DD";
              e.target.style.boxShadow = "none";
            }}
          />
          <SoftTypography
            sx={{ fontFamily: "DM Sans", fontSize: "11.5px", color: "#98A2B3", mt: "5px" }}
          >
            Powers work-anniversary automations.
          </SoftTypography>
        </SoftBox>
      </SoftBox>

      {/* Footer */}
      <SoftBox
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
          padding: "18px 22px 20px",
        }}
      >
        <SoftButton
          onClick={onClose}
          disabled={saving}
          sx={{
            background: "#fff",
            border: "1px solid #D0D5DD",
            borderRadius: "8px",
            color: "#344054",
            fontSize: "13px",
            fontWeight: 600,
            textTransform: "none",
            padding: "0 18px",
            height: "40px",
            boxShadow: "none",
            "&:hover": { background: "#F9FAFB" },
          }}
        >
          Cancel
        </SoftButton>
        <SoftButton
          onClick={handleSave}
          disabled={saving}
          sx={{
            background: BRAND,
            border: 0,
            borderRadius: "8px",
            color: "#FFFFFF !important",
            fontSize: "13px",
            fontWeight: 600,
            textTransform: "none",
            padding: "0 20px",
            height: "40px",
            boxShadow: "none",
            "&:hover": { background: BRAND, filter: "brightness(0.96)" },
          }}
        >
          {saving ? "Saving…" : "Save details"}
        </SoftButton>
      </SoftBox>
    </Dialog>
  );
};

export default EditPlayerDetails;
